import React from 'react';
import { CalendarIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { isoDay, shiftDay, todayIso, fmtDayShort } from '@/lib/format.js';

// Shared range picker in the header. Presets cover the common ops windows; the
// registry Calendar (react-day-picker range mode) handles arbitrary ranges. Emits
// { from, to } as ISO days.
function monthStartIso(d) {
  const x = new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-01`;
}
function lastMonthRange() {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const last = new Date(now.getFullYear(), now.getMonth(), 0);
  return { from: isoDay(first), to: isoDay(last) };
}

export function presetRange(key) {
  const to = todayIso();
  if (key === 'this-month') return { from: monthStartIso(new Date()), to };
  if (key === '7d') return { from: shiftDay(to, -6), to };
  if (key === '30d') return { from: shiftDay(to, -29), to };
  if (key === '45d') return { from: shiftDay(to, -44), to };
  if (key === 'last-month') return lastMonthRange();
  return { from: shiftDay(to, -29), to };
}

const PRESETS = [
  { key: 'this-month', label: 'This month' },
  { key: 'last-month', label: 'Last month' },
  { key: '7d', label: 'Last 7 days' },
  { key: '30d', label: 'Last 30 days' },
  { key: '45d', label: 'Last 45 days' },
];

function toDate(iso) { return iso ? new Date(iso + 'T00:00:00') : undefined; }

export default function DateRangePicker({ range, onChange }) {
  const [open, setOpen] = React.useState(false);
  const [sel, setSel] = React.useState({ from: toDate(range.from), to: toDate(range.to) });

  React.useEffect(() => { setSel({ from: toDate(range.from), to: toDate(range.to) }); }, [range.from, range.to]);

  const apply = (r) => { onChange(r); setOpen(false); };
  const applyCalendar = () => {
    if (sel && sel.from) apply({ from: isoDay(sel.from), to: isoDay(sel.to || sel.from) });
  };

  const label = range.from === range.to
    ? fmtDayShort(range.from)
    : `${fmtDayShort(range.from)} – ${fmtDayShort(range.to)}`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="tabular-nums">
          <CalendarIcon className="size-3.5" /> {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-auto p-0">
        <div className="flex flex-col sm:flex-row">
          <div className="flex flex-col gap-1 p-2 sm:w-40">
            {PRESETS.map((p) => (
              <Button
                key={p.key} variant="ghost" size="sm"
                className="justify-start font-normal"
                onClick={() => apply(presetRange(p.key))}
              >
                {p.label}
              </Button>
            ))}
          </div>
          <Separator orientation="vertical" className="hidden sm:block" />
          <div className="p-2">
            <Calendar
              mode="range"
              numberOfMonths={1}
              defaultMonth={sel.from}
              selected={sel}
              onSelect={setSel}
              disabled={{ after: new Date() }}
            />
            <div className="flex justify-end gap-2 px-1 pb-1">
              <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
              <Button size="sm" onClick={applyCalendar} disabled={!sel || !sel.from}>Apply</Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
